import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { userAPI } from "@/api/users";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";

const emptyProfile = {
  userId: "",
  username: "",
  empNo: "",
  email: "",
  roles: [],
  roleNames: [],
  defaultMenuId: "",
  defaultMenuName: "",
  language: "ko",
  oldPassword: "",
  newPassword: "",
  confirmPassword: "",
};

export function MyProfileDialog({ open, onOpenChange }) {
  const { updateCurrentUser } = useAuth();
  const { setLanguage, t } = useI18n();
  const [profile, setProfile] = useState(emptyProfile);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    userAPI
      .getMyProfile({ showGlobalLoading: false })
      .then((response) =>
        setProfile({ ...emptyProfile, ...(response.data || response) })
      )
      .catch(() => toast.error(t("profile.loadFailed")));
  }, [open, t]);

  const updateField = (field) => (event) =>
    setProfile((current) => ({ ...current, [field]: event.target.value }));

  const handleSave = async () => {
    if (profile.newPassword && profile.newPassword !== profile.confirmPassword) {
      toast.error(t("profile.passwordMismatch"));
      return;
    }
    if (profile.newPassword && !profile.oldPassword) {
      toast.error(t("profile.currentPasswordRequired"));
      return;
    }

    setSaving(true);
    try {
      const response = await userAPI.updateMyProfile({
        username: profile.username,
        empNo: profile.empNo,
        email: profile.email,
        defaultMenuId: profile.defaultMenuId || null,
        language: profile.language,
      });
      const updated = response.data || response;

      if (profile.newPassword) {
        await userAPI.changeMyPassword({
          oldPassword: profile.oldPassword,
          newPassword: profile.newPassword,
        });
      }

      updateCurrentUser(updated);
      setLanguage(updated.language || profile.language);
      toast.success(t("profile.saved"));
      onOpenChange(false);
    } catch (error) {
      toast.error(t("profile.saveFailed"), {
        description: error.response?.data?.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("profile.title")}</DialogTitle>
          <DialogDescription>{t("profile.description")}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2 sm:grid-cols-2">
          <Field label={t("profile.account")}>
            <Input value={profile.userId} disabled />
          </Field>
          <Field label={t("profile.username")}>
            <Input value={profile.username} onChange={updateField("username")} />
          </Field>
          <Field label={t("profile.empNo")}>
            <Input value={profile.empNo || ""} onChange={updateField("empNo")} />
          </Field>
          <Field label={t("profile.email")}>
            <Input
              type="email"
              value={profile.email || ""}
              onChange={updateField("email")}
            />
          </Field>
          <Field label={t("profile.roles")}>
            <Input
              value={(profile.roleNames?.length
                ? profile.roleNames
                : profile.roles
              )?.join(", ") || ""}
              disabled
            />
          </Field>
          <Field label={t("profile.defaultMenu")}>
            <Input
              value={profile.defaultMenuId || ""}
              onChange={updateField("defaultMenuId")}
              placeholder={profile.defaultMenuName || ""}
            />
          </Field>
          <Field label={t("profile.language")}>
            <select
              className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
              value={profile.language || "ko"}
              onChange={updateField("language")}
            >
              <option value="ko">{t("common.korean")}</option>
              <option value="en">{t("common.english")}</option>
              <option value="zh-tw">{t("common.traditionalChinese")}</option>
              <option value="zh-cn">{t("common.simplifiedChinese")}</option>
            </select>
          </Field>
          <div />
          <Field label={t("profile.currentPassword")}>
            <Input
              type="password"
              value={profile.oldPassword}
              onChange={updateField("oldPassword")}
              autoComplete="current-password"
            />
          </Field>
          <Field label={t("profile.newPassword")}>
            <Input
              type="password"
              value={profile.newPassword}
              onChange={updateField("newPassword")}
              autoComplete="new-password"
            />
          </Field>
          <Field label={t("profile.confirmPassword")}>
            <Input
              type="password"
              value={profile.confirmPassword}
              onChange={updateField("confirmPassword")}
              autoComplete="new-password"
            />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t("profile.saving") : t("profile.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
